/**
 * /close — Month-end close calendar.
 *
 * Two stacks:
 *   1. Open close periods (status != closed) — what's actively in flight.
 *      Sorted by target_close_date ascending so the next-due is at top.
 *   2. Fiscal periods without a close opened yet — last 12 months,
 *      newest first. Each shows an "Open close" button.
 *
 * Why we don't combine them: the cognitive load of "open close" vs.
 * "open period" is real. Separating the lists lets a controller scan
 * the top stack for action and the bottom stack for backlog.
 *
 * Actions:
 *   - Click a row → /close/$periodId
 *   - "Open close" on an unopened fiscal period → POST /api/close/{id}/open
 *   - "Manage templates" link to /close/templates
 */
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  LinearProgress,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SettingsIcon from "@mui/icons-material/Settings";

import { api, errorMessage } from "@/lib/api";

// ─── Types ────────────────────────────────────────────────────────────────

type ClosePeriodListItem = {
  id: string;
  fiscal_period_id: string;
  status: "open" | "in_progress" | "pending_signoff" | "closed";
  target_close_date: string;
  actual_close_date: string | null;
  pct_complete: number;
  days_to_target: number | null;
};

type FiscalPeriod = {
  id: string;
  fiscal_year_id: string;
  period_no: number;
  start_date: string;
  end_date: string;
  status: "open" | "closed" | "locked";
};

// ─── Route ────────────────────────────────────────────────────────────────

export const Route = createFileRoute("/_auth/close/")({
  component: CloseIndexPage,
});

function CloseIndexPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [toast, setToast] = useState<{ msg: string; severity: "success" | "error" | "info" } | null>(null);

  const closeQuery = useQuery({
    queryKey: ["close", "list"],
    queryFn: () => api<ClosePeriodListItem[]>("/api/close"),
  });

  const periodsQuery = useQuery({
    queryKey: ["periods", "all"],
    queryFn: () => api<FiscalPeriod[]>("/api/periods"),
  });

  const openClose = useMutation({
    mutationFn: (fiscalPeriodId: string) =>
      api(`/api/close/${fiscalPeriodId}/open`, { method: "POST" }),
    onSuccess: (_data, fiscalPeriodId) => {
      qc.invalidateQueries({ queryKey: ["close"] });
      setToast({ msg: "Close period opened — 15-item checklist instantiated", severity: "success" });
      navigate({ to: "/close/$periodId", params: { periodId: fiscalPeriodId } });
    },
    onError: (e) => setToast({ msg: errorMessage(e), severity: "error" }),
  });

  const closePeriods = closeQuery.data ?? [];
  const allPeriods = periodsQuery.data ?? [];

  // Periods that don't have a close opened yet, scoped to last 12 months
  // and ended (no point opening a close for a period that hasn't ended).
  const cutoff = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 366);
    return d.toISOString().slice(0, 10);
  }, []);
  const today = new Date().toISOString().slice(0, 10);
  const closedFiscalIds = useMemo(
    () => new Set(closePeriods.map((c) => c.fiscal_period_id)),
    [closePeriods],
  );

  const unopened = useMemo(
    () =>
      allPeriods
        .filter((p) => !closedFiscalIds.has(p.id))
        .filter((p) => p.end_date >= cutoff)
        .filter((p) => p.end_date <= today)  // hide periods that haven't ended
        .sort((a, b) => b.end_date.localeCompare(a.end_date))
        .slice(0, 12),
    [allPeriods, closedFiscalIds, cutoff, today],
  );

  const activeCloses = closePeriods
    .filter((c) => c.status !== "closed")
    .sort((a, b) => a.target_close_date.localeCompare(b.target_close_date));

  const closedCloses = closePeriods
    .filter((c) => c.status === "closed")
    .sort((a, b) => (b.actual_close_date || "").localeCompare(a.actual_close_date || ""));

  const isLoading = closeQuery.isLoading || periodsQuery.isLoading;

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5">Month-End Close</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720 }}>
            Run your monthly close from a single checklist. Items grouped by category (recon, subledger, JE review, revenue, depreciation, final review). Auto-verify watches for items it can confirm itself — depreciation runs, posted manual JEs, ASC 606 recognition.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<SettingsIcon />}
          component={Link}
          to={"/close/templates" as never}
        >
          Manage templates
        </Button>
      </Stack>

      {isLoading ? (
        <Box sx={{ textAlign: "center", py: 6 }}><CircularProgress /></Box>
      ) : (
        <Stack spacing={3}>
          {/* Active closes — top of mind, action needed */}
          <Card>
            <CardHeader
              title={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="h6">In flight</Typography>
                  {activeCloses.length > 0 && (
                    <Chip size="small" label={activeCloses.length} />
                  )}
                </Stack>
              }
              subheader="Close periods that are open, in progress, or awaiting sign-off."
            />
            <CardContent sx={{ pt: 0 }}>
              {activeCloses.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No close periods in flight. Open one from the list below.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Period</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Target close</TableCell>
                      <TableCell sx={{ minWidth: 200 }}>Progress</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activeCloses.map((cp) => (
                      <CloseRow key={cp.id} cp={cp} fiscalPeriod={allPeriods.find((p) => p.id === cp.fiscal_period_id)} />
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Unopened fiscal periods — ready to be closed */}
          {unopened.length > 0 && (
            <Card>
              <CardHeader
                title={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="h6">Ready to close</Typography>
                    <Chip size="small" label={unopened.length} />
                  </Stack>
                }
                subheader="Fiscal periods that have ended but no close has been opened yet."
              />
              <CardContent sx={{ pt: 0 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Period</TableCell>
                      <TableCell>Ended</TableCell>
                      <TableCell>Period status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {unopened.map((p) => (
                      <TableRow key={p.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {formatPeriodLabel(p)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {p.end_date}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={p.status} variant="outlined" />
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="contained"
                            disabled={openClose.isPending}
                            onClick={() => openClose.mutate(p.id)}
                          >
                            Open close
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Recently closed — for reference + reopening if needed */}
          {closedCloses.length > 0 && (
            <Card>
              <CardHeader
                title={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="h6">Recently closed</Typography>
                    <Chip size="small" label={closedCloses.length} />
                  </Stack>
                }
                subheader="Signed-off closes — read-only audit trail."
              />
              <CardContent sx={{ pt: 0 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Period</TableCell>
                      <TableCell>Closed</TableCell>
                      <TableCell>Target → Actual</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {closedCloses.slice(0, 6).map((cp) => {
                      const fp = allPeriods.find((p) => p.id === cp.fiscal_period_id);
                      const variance = cp.actual_close_date
                        ? daysBetween(cp.target_close_date, cp.actual_close_date)
                        : null;
                      return (
                        <TableRow key={cp.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {fp ? formatPeriodLabel(fp) : "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {cp.actual_close_date || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {cp.target_close_date} → {cp.actual_close_date || "—"}
                            </Typography>
                            {variance !== null && (
                              <Typography variant="caption" color={variance > 0 ? "error.main" : "success.main"}>
                                {variance > 0 ? `${variance}d late` : variance < 0 ? `${Math.abs(variance)}d early` : "on time"}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              component={Link}
                              to={"/close/$periodId" as never}
                              params={{ periodId: cp.fiscal_period_id } as never}
                              endIcon={<OpenInNewIcon />}
                            >
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* True empty state — no closes opened anywhere */}
          {activeCloses.length === 0 && unopened.length === 0 && closedCloses.length === 0 && (
            <Card>
              <CardContent sx={{ py: 6, textAlign: "center" }}>
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  No fiscal periods found.
                </Typography>
                <Typography variant="caption" color="text.disabled" sx={{ display: "block", mb: 2 }}>
                  Set up your fiscal year first — Accounting → Periodic → Fiscal Periods.
                </Typography>
                <Button variant="contained" component={Link} to={"/periods" as never}>
                  Go to Fiscal Periods
                </Button>
              </CardContent>
            </Card>
          )}
        </Stack>
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast ? <Alert severity={toast.severity}>{toast.msg}</Alert> : <span />}
      </Snackbar>
    </Box>
  );
}

// ─── Row ──────────────────────────────────────────────────────────────────

function CloseRow({ cp, fiscalPeriod }: { cp: ClosePeriodListItem; fiscalPeriod?: FiscalPeriod }) {
  const navigate = useNavigate();
  const overdue = cp.days_to_target !== null && cp.days_to_target < 0;
  const dueSoon = cp.days_to_target !== null && cp.days_to_target >= 0 && cp.days_to_target <= 2;
  return (
    <TableRow
      hover
      sx={{ cursor: "pointer" }}
      onClick={() => navigate({ to: "/close/$periodId", params: { periodId: cp.fiscal_period_id } })}
    >
      <TableCell>
        <Typography variant="body2" fontWeight={600}>
          {fiscalPeriod ? formatPeriodLabel(fiscalPeriod) : "—"}
        </Typography>
      </TableCell>
      <TableCell>
        <StatusChip status={cp.status} />
      </TableCell>
      <TableCell>
        <Typography variant="body2" color={overdue ? "error.main" : dueSoon ? "warning.main" : "text.primary"}>
          {cp.target_close_date}
        </Typography>
        <Typography variant="caption" color={overdue ? "error.main" : "text.secondary"}>
          {cp.days_to_target === null
            ? ""
            : cp.days_to_target < 0
              ? `${Math.abs(cp.days_to_target)}d overdue ⚠`
              : cp.days_to_target === 0
                ? "today"
                : `in ${cp.days_to_target}d`}
        </Typography>
      </TableCell>
      <TableCell>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{ flex: 1 }}>
            <LinearProgress
              variant="determinate"
              value={cp.pct_complete}
              sx={{ height: 8, borderRadius: 1 }}
              color={cp.pct_complete >= 100 ? "success" : cp.pct_complete >= 60 ? "primary" : "warning"}
            />
          </Box>
          <Typography variant="caption" sx={{ minWidth: 40, textAlign: "right" }}>
            {cp.pct_complete.toFixed(0)}%
          </Typography>
        </Stack>
      </TableCell>
      <TableCell align="right">
        <Button
          size="small"
          component={Link}
          to={"/close/$periodId" as never}
          params={{ periodId: cp.fiscal_period_id } as never}
          onClick={(e) => e.stopPropagation()}
        >
          Open
        </Button>
      </TableCell>
    </TableRow>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function StatusChip({ status }: { status: ClosePeriodListItem["status"] }) {
  const config: Record<typeof status, { label: string; color: "default" | "primary" | "warning" | "success" }> = {
    open: { label: "Open", color: "default" },
    in_progress: { label: "In progress", color: "primary" },
    pending_signoff: { label: "Pending sign-off", color: "warning" },
    closed: { label: "Closed", color: "success" },
  };
  const c = config[status];
  return <Chip size="small" color={c.color} label={c.label} />;
}

function formatPeriodLabel(p: FiscalPeriod): string {
  const d = new Date(p.end_date + "T00:00:00");
  return d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

function daysBetween(a: string, b: string): number {
  const ms = new Date(b + "T00:00:00").getTime() - new Date(a + "T00:00:00").getTime();
  return Math.round(ms / 86_400_000);
}
